import { css } from '@emotion/css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Color } from 'css/theme';
import Link from 'next/link';
import { useRouter } from 'next/router';
import * as React from 'react';
import spacetime from 'spacetime';

import { customElementInteraction } from 'bindings/Segment.bs';
import MenuList from 'components/MenuList';
import { useRemindersContext } from './context';
import GiftReminderDeleteConfirmModal from './GiftReminderDeleteConfirmModal';
import { ModalState } from 'pages/reminders/reminders';
import { remove } from 'api/queries';
import logger from 'utils/logger';
import { Type } from 'components/Notification';

const wrapper = css({
  backgroundColor: Color.White,
  '& + &': { marginTop: '16px' },
  padding: '12px',
  position: 'relative',
  boxShadow: '0px 10px 30px rgba(209, 213, 223, 0.5)',
  borderRadius: '10px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  '@media (min-width: 1024px)': {
    padding: '26px',
  },
});

const leftInnerContainer = css({
  display: 'flex',
  width: '80%',
  alignItems: 'center',
});

const rightInnerContainer = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'end',
});

const iconImage = css({
  width: '40px',
  height: '40px',
  marginRight: '12px',
  '@media (min-width: 768px)': {
    width: '55px',
    height: '55px',
    marginRight: '22px',
  },
});

const title = css({
  fontSize: '18px',
  lineHeight: '21px',
  paddingBottom: '6px',
  '@media (min-width: 768px)': {
    fontSize: '24px',
    lineHeight: '28px',
  },
});

const date = css({
  fontSize: '14px',
  lineHeight: '19px',
  color: Color.BorderGray,
  '@media (min-width: 768px)': {
    fontSize: '16px',
    lineHeight: '22px',
  },
  '> span': {
    fontWeight: 600,
  },
});

const icon = css({
  marginLeft: '22px',
});

const link = css({
  color: Color.PrimaryBlue,
  textDecoration: 'underline',
});

const menuWrapper = css({
  position: 'absolute',
  right: '0px',
  top: '-10px',
  zIndex: 10,
});

interface Customer {
  uid: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
}

interface Relationship {
  uid: string;
  title: string;
  hidden: boolean;
}

interface Occasion {
  uid: string;
  title: string;
  notificationHeader: string;
  thumbImage: string;
  hoverImage: string;
  iconImage: string;
  calendarDotColour: string;
  recurrenceType: string;
  hidden: boolean;
  nextOccurrenceInCurrentLocale: null;
}
export interface ReminderListItem {
  uid: string;
  archivedAt: string | null;
  customer: Customer;
  nextOccurrenceDate: string;
  heading: string;
  recipientName: string;
  relationship: Relationship;
  occasion: Occasion;
  occasionDate: string;
  sendViaEmail: boolean;
  sendViaSms: boolean;
  orderNumber: string | null;
}

interface Props {
  item: ReminderListItem;
  showMenu: boolean;
  setShowMenu: (id: string) => void;
  menuRef: any;
}

const getDisplayDate = (ref: Date) => {
  const rtf = new Intl.RelativeTimeFormat('en', { style: 'short' });
  const today = new Date();

  const diff = Math.floor((ref.setHours(10) - today.setHours(9)) / (1000 * 3600 * 24));

  const getDiffStr = () => (diff === 0 ? 'today' : rtf.format(diff, 'day'));
  return <span>{` - It's ${getDiffStr()}!`}</span>;
};

const GiftReminderItem = ({ item, showMenu, setShowMenu, menuRef }: Props) => {
  const { setModalState, getReminders, setNotification } = useRemindersContext();
  const router = useRouter();
  const itemDate = new Date(item.nextOccurrenceDate);
  const displayDate = spacetime(itemDate).format('{date-pad} {month-short}');

  const handleDelete = () => {
    remove(`api/v3/reminders/${item.uid}/`, undefined, true)
      .then(() => {
        setNotification({
          type: Type.Success,
          title: 'Your reminder has been deleted.',
        });
        getReminders();
      })
      .catch(error => {
        logger.error(
          {
            err: error,
            source: 'Reminders API',
          },
          'error: unable to delete reminder'
        );
      });
  };

  const handleEdit = () => {
    customElementInteraction({
      element: 'my_reminders_edit',
      reminder_id: item.uid,
      path: router.pathname,
    });
    setShowMenu(null);
    setModalState({
      state: ModalState.Edit,
      initValues: {
        name: item.recipientName,
        occasion: {
          value: item.occasion.uid,
          label: item.occasion.title,
          iconUrl: item.occasion.thumbImage,
          hoverIconUrl: item.occasion.hoverImage,
        },
        relationship: {
          value: item.relationship.uid,
          label: item.relationship.title,
        },
        date: new Date(item.nextOccurrenceDate),
        sendViaEmail: item.sendViaEmail,
        sendViaSms: item.sendViaSms,
      },
      uid: item.uid,
    });
  };

  return (
    <GiftReminderDeleteConfirmModal
      title="Delete reminder"
      description="Once you delete this reminder it cannot be undone."
    >
      {confirm => {
        return (
          <div className={wrapper}>
            {showMenu && (
              <div className={menuWrapper}>
                <MenuList
                  ref={menuRef}
                  items={[
                    {
                      text: 'Edit',
                      value: {
                        id: 'edit',
                      },
                      onClick: handleEdit,
                    },
                    {
                      text: 'Delete',
                      value: {
                        id: 'delete',
                      },
                      onClick: confirm(handleDelete, {
                        element: 'my_reminders_delete',
                        reminder_id: item.uid,
                        path: router.pathname,
                      }),
                    },
                  ]}
                />
              </div>
            )}
            <div className={leftInnerContainer}>
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img src={item.occasion.iconImage} alt={item.occasion.title} className={iconImage} />
              <div>
                <p className={title}>{item.heading}</p>
                <p className={date}>
                  {displayDate} {getDisplayDate(itemDate)}
                </p>
              </div>
            </div>
            <div className={rightInnerContainer}>
              <Link href="/store" passHref>
                <button
                  className={link}
                  onClick={() => {
                    customElementInteraction({
                      element: 'my_reminders_shop',
                      path: router.pathname,
                    });
                  }}
                >
                  Shop
                </button>
              </Link>
              <button
                className={`${icon} menu-btn`}
                onClick={() => {
                  setShowMenu(item.uid);
                }}
              >
                <FontAwesomeIcon icon={['fas', 'ellipsis-v']} pointerEvents="none" />
              </button>
            </div>
          </div>
        );
      }}
    </GiftReminderDeleteConfirmModal>
  );
};

export default GiftReminderItem;
